import { useState, useEffect } from 'react';

interface BreakdownData {
  day_earnings: number;
  after_hours_earnings: number;
  callout_fees: number;
}

interface Props {
  timeframe: { from: string; to: string };
}

export default function EarningsBreakdown({ timeframe }: Props) {
  const [data, setData] = useState<BreakdownData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBreakdown();
  }, [timeframe]);

  const fetchBreakdown = async () => {
    try {
      setLoading(true);
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const token = localStorage.getItem('supabase_token');

      const url = new URL(`${supabaseUrl}/functions/v1/get-provider-earnings-summary`);
      url.searchParams.append('from', timeframe.from);
      url.searchParams.append('to', timeframe.to);

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setData({
          day_earnings: result.day_earnings,
          after_hours_earnings: result.after_hours_earnings,
          callout_fees: result.callout_fees,
        });
      }
    } catch (error) {
      console.error('Error fetching breakdown:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const breakdownItems = [
    {
      label: 'Day earnings',
      value: `£${data.day_earnings.toFixed(2)}`,
      description: 'Jobs completed during standard hours',
      icon: 'ri-sun-line',
      color: 'text-amber-600',
    },
    {
      label: 'After-hours earnings',
      value: `£${data.after_hours_earnings.toFixed(2)}`,
      description: 'Jobs completed between 6pm and 6am',
      icon: 'ri-moon-line',
      color: 'text-indigo-600',
    },
    {
      label: 'Late call-out fees',
      value: `£${data.callout_fees.toFixed(2)}`,
      description: 'One-off fees for short-notice after-hours jobs',
      icon: 'ri-alarm-warning-line',
      color: 'text-orange-600',
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Earnings Breakdown</h3>
      <div className="space-y-6">
        {breakdownItems.map((item, index) => (
          <div key={index} className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center`}>
                <i className={`${item.icon} ${item.color} text-xl`}></i>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">{item.label}</div>
                <div className="text-xs text-gray-500 mt-1">{item.description}</div>
              </div>
            </div>
            <div className="text-xl font-bold text-gray-900">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
